import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { TFPageContent } from '@tf/shared';
import {
	ActionIcon,
	Box,
	Button,
	EntityBadge,
	Flex,
	Icon,
	Modal,
	ModalFooter,
	Stack,
	TFCard,
	TFNotifier,
	TFText,
	Title,
	useDisclosureWithParams,
} from '@tf/ui';
import type React from 'react';
import { useApplication } from '@/components/application';
import {
	ActionButton,
	ActionCard,
	MarkdownPageIntro,
	useGetConnectionLabels,
	useGetEntityLabel,
	useMenu,
} from '@/components/shared';
import { AddConnectionModal, type AddConnectionModalProps } from '@/components/structure/AddConnectionModal';
import { FormModal } from '@/components/structure/FormModal';
import { hasUniqEntityConnected } from '@/components/structure/hasUniqEntityConnected';
import { useStructureEntities } from '@/components/structure/useStructureEntities';
import { useDisconnectMutation } from '@/core/api';
import { useDefinitions } from '@/core/providers';
import { useApplicationStructureParams } from '@/routes/endClientArea/params';

export const FlatStructure: React.FC = () => {
	const { _ } = useLingui();

	const application = useApplication();
	const { menuItems } = useMenu();
	const formIndex = menuItems.findIndex((item) => item.isSelected);
	const currentMenuItem = menuItems.find((item) => item.isSelected);

	const deleteAccountModal = useDisclosureWithParams<{ graphId: number; name: string }>();
	const addConnectionModal = useDisclosureWithParams<AddConnectionModalProps>();
	const formModal = useDisclosureWithParams<{ entityId?: number }>();

	const { applicationConnectionKind } = useApplicationStructureParams();

	const structure = useStructureEntities(applicationConnectionKind);
	const entities = structure.filter((entity) => entity.entityId !== application.account.graphNodeId);
	const applicationConnection = structure
		.find((entity) => entity.entityId === application.account.graphNodeId)
		?.connections.find((c) => c.connectionKind === applicationConnectionKind);

	const { defs } = useDefinitions();

	const entityToEdit = entities.find((e) => e.entityId === formModal.params?.entityId);
	const entitySelfForms = entityToEdit?.forms.map((f) => f.form) ?? [];
	const entityConnectionForms =
		applicationConnection?.connectedEntities
			.find((e) => e.entityId === entityToEdit?.entityId)
			?.forms.map((f) => f.form) ?? [];
	const entityForms = [...entitySelfForms, ...entityConnectionForms];

	const disconnectMutation = useDisconnectMutation();
	const removeConnection = () => {
		if (!deleteAccountModal.params?.graphId) return;
		disconnectMutation.mutate(
			{
				parentEntityId: application.account.graphNodeId,
				connectionKind: applicationConnectionKind,
				entityId: deleteAccountModal.params.graphId,
			},
			{
				onSuccess: () => {
					TFNotifier.success(
						_(
							msg({
								id: 'flatStructure.notifications.success',
								message: 'Successfully deleted',
							}),
						),
					);
					deleteAccountModal.close();
				},
				onError: () => {
					TFNotifier.error(
						_(
							msg({
								id: 'flatStructure.notifications.fail',
								message: 'Failed to delete',
							}),
						),
					);
				},
			},
		);
	};

	const getConnectionLabels = useGetConnectionLabels();
	const getLabelsForEntity = getConnectionLabels(application.account.entityKind);
	const connectionLabel = getLabelsForEntity(applicationConnectionKind)?.singular;

	const accountName = deleteAccountModal.params?.name;

	const connectionDescription =
		defs.accounts[application.account.entityKind].connections?.[applicationConnectionKind]?.description;
	const getEntityLabel = useGetEntityLabel();

	const hasConnected = hasUniqEntityConnected(structure, applicationConnectionKind);

	return (
		<Stack flex="1" maw="50rem">
			<TFPageContent maw="50rem" m="0">
				<Stack mb="16px" gap="16px" align="center">
					<Title mt="16px" fz="32px" lh="40px" fw="600" order={2}>
						{currentMenuItem?.label}
					</Title>
					{connectionDescription && <MarkdownPageIntro text={connectionDescription} />}
				</Stack>
				<TFCard innerPadding="1.5rem" sx={{ flex: 1 }}>
					<Stack gap="12px">
						{entities.map((entity) => {
							const statuses = entity?.forms.map((f) => f.formStatus.structure);
							const hasErrors = statuses?.some((s) => s !== 'OK');
							return (
								<ActionCard
									error={
										hasErrors
											? _(
													msg({
														id: 'applicationNominatedAccountsPage.errors.requiredInfo',
														message: 'Please add all required information',
													}),
												)
											: undefined
									}
									icon={
										<Flex
											justify="center"
											align="center"
											w="36px"
											h="36px"
											bg={hasErrors ? '#FEE2E2' : '#F3F4F6'}
											mr="12px"
											sx={{ borderRadius: '50%' }}
										>
											{entity.entityKind === 'INDIVIDUAL_ENTITY' ? (
												<Icon.IconUser size={24} />
											) : (
												<Icon.IconBuildingSkyscraper size={24} />
											)}
										</Flex>
									}
									key={entity.entityId}
									title={entity.entityName}
									onClick={() => {
										if (!entity) return;
										formModal?.open({ entityId: entity.entityId });
									}}
								>
									<Flex sx={{ flex: 1 }} align="center" justify="flex-end">
										<Box>
											<EntityBadge kind={getEntityLabel(entity.entityKind)} />
										</Box>
										<ActionIcon
											ml="12px"
											variant="transparent"
											color="#6B7280"
											onClick={(e) => {
												e.stopPropagation();
												deleteAccountModal.open({ graphId: entity.entityId, name: entity.entityName });
											}}
										>
											<Icon.IconTrash size={18} stroke={1.5} />
										</ActionIcon>
									</Flex>
								</ActionCard>
							);
						})}
						{!hasConnected && (
							<ActionButton
								title={_(
									msg({
										id: 'flatStructure.actionButton',
										message: `Add ${connectionLabel}`,
									}),
								)}
								onClick={() =>
									addConnectionModal?.open({
										connectionKind: applicationConnectionKind,
										parentEntityKind: application.account.entityKind,
										parentEntityName: application.account.name,
										parentEntityId: application.account.graphNodeId,
										withSuccessMessage: false,
									})
								}
							/>
						)}
					</Stack>
				</TFCard>
			</TFPageContent>
			<TFCard mt="16px" maw="50rem" innerPadding="12px">
				<Flex direction="row-reverse">
					<Button
						mih="36px"
						onClick={() => {
							const nextForm = menuItems[formIndex + 1];
							nextForm.onClick();
						}}
					>
						<Trans id="applicationForm.saveAndContinueButton">Save and continue</Trans>
					</Button>
				</Flex>
			</TFCard>

			<FormModal
				fullScreen
				title={
					_(
						msg({
							id: 'flatStructure.formModal.title',
							message: `Provide information about`,
						}),
					) + entityToEdit?.entityName
				}
				opened={Boolean(formModal.isOpen && entityForms.length > 0)}
				onClose={formModal.close}
				contentProps={{
					forms: entityForms ?? [],
					onClose: formModal.close,
				}}
			/>
			<AddConnectionModal
				size="500px"
				opened={addConnectionModal.isOpen}
				onClose={addConnectionModal.close}
				title={`${_(
					msg({
						id: 'flatStructure.addConnectionModal.title',
						message: `Add ${connectionLabel} for `,
					}),
				)} ${application.account.name}`}
				contentProps={
					addConnectionModal.params
						? {
								...addConnectionModal.params,
								onConnectionAdded: (id) => {
									formModal.open({ entityId: id });
								},
							}
						: null
				}
			/>

			<Modal
				size="600px"
				title={_(
					msg({
						id: 'flatStructure.deleteModal.title',
						message: `Delete ${accountName} account?`,
					}),
				)}
				opened={deleteAccountModal.isOpen}
				onClose={deleteAccountModal.close}
			>
				<Box m="16px">
					<TFText size="14px">
						{_(
							msg({
								id: 'flatStructure.deleteModal.subtitle',
								message: `Once deleted, the account cannot be restored`,
							}),
						)}
					</TFText>
				</Box>
				<ModalFooter
					submitLabel={_(
						msg({
							id: 'flatStructure.deleteModal.submitLabel',
							message: `Delete account`,
						}),
					)}
					cancelLabel={_(
						msg({
							id: 'flatStructure.deleteModal.cancelLabel',
							message: `Keep account`,
						}),
					)}
					onCancel={deleteAccountModal.close}
					onSubmit={removeConnection}
					submitButtonColor="red"
					isLoading={disconnectMutation.isPending}
				/>
			</Modal>
		</Stack>
	);
};
